<template>
  <div>
    <b-row>
      <b-col
        md="8"
        class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
      >
        <p class="h5 mt-50 mr-75 ml-50">
          일별/누적
        </p>
        <b-form-radio-group
          v-model="query.isPrefixSum"
        >
          <b-form-radio :value="false">
            일별 (기간:최대30일)
          </b-form-radio>
          <b-form-radio :value="true">
            누적 (기간:전체누적)
          </b-form-radio>
        </b-form-radio-group>
        <b-form-datepicker
          v-if="!query.isPrefixSum"
          v-model="query.startDate"
          class="el-def"
          placeholder="조회기간(Fr)"
          style="width: 240px"
        />
        <label
          v-if="!query.isPrefixSum"
          class="ml-50 mr-50"
        >~</label>
        <b-form-datepicker
          v-if="!query.isPrefixSum"
          v-model="query.endDate"
          class="el-def"
          placeholder="조회기간(To)"
          style="width: 240px"
        />
        <label class="ml-50">(*당일데이터 조회 불가)</label>
      </b-col>
      <b-col
        md="4"
        class="d-flex align-items-center justify-content-end"
      >
        <b-button
          variant="primary"
          class="btn-search"
          @click.prevent="fetchData()"
        >
          조회
        </b-button>
        <b-button
          variant="outline-dark"
          class="btn-search ml-1"
          @click.prevent="excelDownload()"
        >
          <feather-icon
            icon="DownloadIcon"
            class="mr-50"
          />
          <span>엑셀 다운로드</span>
        </b-button>
      </b-col>
    </b-row>
    <b-row class="mt-2">
      <b-col
        cols="12"
        md="4"
        class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
      >
        <b-table-simple
          bordered
          responsive
          sticky-header
        >
          <colgroup>
            <col style="width: 130px">
            <col style="width: 120px">
          </colgroup>
          <b-thead head-variant="light">
            <b-tr>
              <b-th>
                구분
              </b-th>
              <b-th>
                유입 수
              </b-th>
            </b-tr>
          </b-thead>
          <b-tbody v-if="data.length > 0">
            <b-tr
              v-for="(m, idx) in data"
              :key="`tr-${idx}`"
            >
              <b-td>{{ m.hour }}</b-td>
              <b-td>{{ m.entryTotalCount | $numberFormatter }}</b-td>
            </b-tr>
          </b-tbody>

          <b-tbody v-else>
            <b-tr>
              <b-td colspan="2">
                <div
                  role="alert"
                  aria-live="polite"
                >
                  <div class="text-center my-2">
                    일치하는 데이터가 없습니다.
                  </div>
                </div>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-col>
      <b-col
        cols="12"
        md="8"
      >
        <b-card
          title=" 닥터인포 시간대별 유입 수"
          style="text-align: center;"
        >
          <LineChart
            :height="200"
            :data="chartData"
            :option="chartSetting.lineChart.option"
            :plugins="chartPlugin"
          />
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import axios from '@axios'
import {
  BTableSimple, BThead, BTbody, BTr, BTh, BTd,
} from 'bootstrap-vue'
import { onMounted, ref, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import LineChart from '@core/components/charts/chartjs/ChartjsComponentLineChart.vue'
import chartSetting from '@core/components/charts/chartjs/chartSetting'
import ExcelJS from "exceljs"
import { saveAs } from "file-saver"

export default {
  components: {
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    LineChart,
  },
  setup() {
    const data = ref([])

    onMounted(() => {
      fetchData()
    })

    const toast = useToast()
    const pushToast = (variant, title) => {
      let icon

      if (variant === 'success') {
        icon = 'CheckCircleIcon'
      } else {
        icon = 'AlertTriangleIcon'
      }

      toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      })
    }

    watch([data], () => {
      rendChart()
    })

    const query = ref({
      startDate: dayjs().add(-7, 'day').format('YYYY-MM-DD'),
      endDate: dayjs().add(-1, 'day').format('YYYY-MM-DD'),
      isPrefixSum: false,
    })

    const fetchData = () => {
      const frDate = dayjs(query.value.startDate)
      const toDate = dayjs(query.value.endDate)

      if (query.value.isPrefixSum && Math.abs(frDate.diff(toDate, 'day')) > 365) {
        pushToast('warning', '조회 기간은 최대 1년을 넘을 수 없습니다.')
        return
      }
      if (query.value.isPrefixSum === false && Math.abs(frDate.diff(toDate, 'day')) > 30) {
        pushToast('warning', '조회 기간은 최대 30일을 넘을 수 없습니다.')
        return
      }

      axios.get('/fa/stats-entry/hour', {
        params: {
          startDate: query.value.startDate,
          endDate: query.value.endDate,
          isPrefixSum: query.value.isPrefixSum,
        },
      })
        .then(rs => {
          data.value = rs.data
        })
        .catch(() => {
          pushToast('danger', '데이터 조회 중 오류가 발생하였습니다.')
        })
    }

    const chartData = ref({
      labels: [],
      datasets: [],
    })

    const chartPlugin = [{
      beforeInit: chart => {
        // eslint-disable-next-line no-param-reassign
        chart.legend.afterFit = () => {
          // eslint-disable-next-line no-param-reassign
          chart.legend.height += 20
        }
      },
    }]

    const rendChart = () => {
      if (data.value == null || data.value.length === 0) return

      const rawData = data.value
      const xLabels = rawData.map(item => item.hour)
      const totalData = rawData.map(item => item.entryTotalCount)

      chartData.value = {
        labels: xLabels,
        datasets: [
          {
            data: totalData,
            label: '유입 수',
            pointStyle: 'circle',
            fill: false,
            borderColor: '#666ee8',
          },
        ],
      }
    }

    const excelDownload = async () => {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet("sheet1")

      const columns = [
        { header: "구분", key: "date", width: 20 },
        {
          header: "유입 수", key: "entryTotalCount", width: 20, style: { numFmt: '#,##0' },
        },
      ]

      worksheet.columns = columns

      // eslint-disable-next-line array-callback-return
      data.value.map(e => {
        const record = {
          date: e.hour,
          entryTotalCount: e.entryTotalCount,
        }

        worksheet.addRow(record)
      })

      worksheet.eachRow({ includeEmpty: true }, (row, number) => {
        row.eachCell({ includeEmpty: true }, cell => {
          if (number === 1) {
            // eslint-disable-next-line no-param-reassign
            cell.font = {
              bold: true,
            }

            // eslint-disable-next-line no-param-reassign
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'D3D3D3' },
            }
          }

          // eslint-disable-next-line no-param-reassign
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          }

          // eslint-disable-next-line no-param-reassign
          cell.alignment = {
            vertical: 'middle',
            horizontal: 'left',
          }
        })
      })

      const mimeType = {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], mimeType)

      saveAs(blob, "시간대별_유입현황.xlsx")
    }

    return {
      data,
      query,
      rendChart,
      fetchData,
      chartData,
      chartPlugin,
      chartSetting,
      excelDownload,
    }
  },
}
</script>

<style scoped>
.table-bordered th, td {
  border: 1px solid #dee2e6;
}

.table th {
  vertical-align: middle;
}
</style>
